import type { NrJsonResponse } from './nr-station-load.ts';

export interface NrDataLoad {
  // nr-berths.json: without it there are no National Rail trains.
  readonly berths: unknown;
  // nr-track.json, or null: without it the recorded step paths are drawn.
  readonly track: unknown;
}

const RETRY_MS = 3000;
const RETRY_MAX_MS = 60_000;

// The trains' two static files, for one generation (nr-ui.ts start). The
// track is optional, so each failure of its own (a refused request, a bad
// body) is null, never the berths' failure. The berths are retried after 3,
// 6, 12 s... (at most a minute apart) until they load or the generation is
// over (null: leaving London stops the retries).
export function loadNrData(
  fetchJson: (url: string) => Promise<NrJsonResponse>,
  berthsUrl: string,
  trackUrl: string,
  current: () => boolean,
): Promise<NrDataLoad | null> {
  const track = fetchJson(trackUrl)
    .then((r) => (r.ok ? r.json() : null))
    .catch(() => null);
  async function attempt(failures: number): Promise<NrDataLoad | null> {
    try {
      const r = await fetchJson(berthsUrl);
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const berths = await r.json();
      return current() ? { berths, track: await track } : null;
    } catch (error) {
      if (!current()) return null;
      const delay = Math.min(RETRY_MS * 2 ** failures, RETRY_MAX_MS);
      console.warn(`National Rail berths unavailable; retrying in ${delay / 1000} s`, error);
      await new Promise((resolve) => {
        setTimeout(resolve, delay);
      });
      return current() ? attempt(failures + 1) : null;
    }
  }
  return attempt(0);
}
